<template>
    <skeleton-loader v-if="loading" type="week-dots" :count="7" />
    <div v-else class="week-dots">
        <div v-for="(day, i) in week" :key="i" class="wday">
            <span class="wday-label">{{ getDayInitial(day.date) }}</span>
            <v-tooltip top :color="tooltipBg" content-class="bp-tooltip">
                <template v-slot:activator="{ on }">
                    <div v-on="on" class="wday-dot" :class="dotClass(day)">
                        <span v-if="day.status !== 'none'" class="wday-num">{{ dayNumber(day) }}</span>
                        <span v-if="day.routine2" class="badge-second">2</span>
                    </div>
                </template>
                <div class="tooltip-content">
                    <div class="tt-date">{{ day.date }}</div>
                    <div v-if="!day.routine1 && !day.routine2" class="tt-empty">{{ $t('common.rest') }}</div>
                    <div v-for="(r, ri) in routinesOfDay(day)" :key="ri">
                        <hr v-if="ri > 0" class="tt-divider">
                        <div v-if="r.title" class="tt-section-title">{{ r.title }}</div>
                        <div v-if="r.routine.isCompetence" class="tt-type">{{ $t('common.competence') }}</div>
                        <div v-for="(row, rowi) in routineRows(r.routine)" :key="rowi" class="tt-row">
                            <span>{{ row.label }}</span><span>{{ row.value }}</span>
                        </div>
                        <template v-if="day.status === 'done'">
                            <div v-if="r.routine.wellness != null" class="tt-row">
                                <span>{{ $t('common.wellness') }}</span>
                                <mini-badge type="wellness" :level="r.routine.wellnessLevel">
                                    {{ r.routine.wellness }}
                                </mini-badge>
                            </div>
                            <div v-if="r.routine.rpe != null" class="tt-row">
                                <span>{{ $t('common.rpe') }}</span>
                                <mini-badge type="rpe" :level="r.routine.rpeLevel">
                                    {{ r.routine.rpe }}
                                </mini-badge>
                            </div>
                        </template>
                    </div>
                </div>
            </v-tooltip>
        </div>
    </div>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import MiniBadge from "@/components/common/MiniBadge";

export default {
    name: "RoutineWeekDots",
    components: { SkeletonLoader, MiniBadge },
    props: {
        week: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
    },
    methods: {
        getDayInitial(date) {
            const vm = this
            if (!date) return ''
            const m = vm.$moment(date, 'DD-MM-YYYY')
            if (vm.$i18n.locale !== 'en' && m.day() === 3) return 'X'
            return m.format('dd').charAt(0).toUpperCase()
        },
        dotClass(day) {
            const map = {
                done: 'g',
                not_realize: 'r',
                partial: 'y',
                to_realize: 'b',
                competence: 'v',
                none: 'gr',
            }
            const base = map[day.status] || 'gr'
            if (base !== 'v' || !day.competenceResult) return base
            return `v ${day.competenceResult}`
        },
        dayNumber(day) {
            const value = day.dateFull ? day.dateFull.slice(8, 10) : (day.date || '').slice(0, 2)
            return Number(value) || ''
        },
        routinesOfDay(day) {
            const vm = this
            const list = []
            if (day.routine1) list.push({ routine: day.routine1, title: day.routine2 ? `${vm.$t('common.routine')} 1` : null })
            if (day.routine2) list.push({ routine: day.routine2, title: `${vm.$t('common.routine')} 2` })
            return list
        },
        routineRows(routine) {
            const vm = this
            const rows = []
            if (routine.isCompetence) {
                if (routine.minutes) rows.push({ label: vm.$t('common.duration'), value: `${routine.minutes} ${vm.$t('common.short_minutes')}` })
                if (routine.effort) rows.push({ label: vm.$t('common.effort'), value: routine.effort })
                if (routine.position) rows.push({ label: vm.$t('common.position'), value: routine.position })
                if (routine.team) rows.push({ label: vm.$t('common.team'), value: routine.team })
                return rows
            }
            rows.push({ label: vm.$tc('common.blocks', routine.blocks), value: routine.blocks })
            rows.push({ label: vm.$t('common.exercises'), value: routine.exercises })
            rows.push({ label: vm.$t('common.duration'), value: `${routine.minutes} ${vm.$t('common.short_minutes')}` })
            return rows
        },
    },
}
</script>

<style scoped>
.week-dots {
    display: flex;
    gap: 4px;
    align-items: center;
}

.wday {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.wday-label {
    font-size: 9px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    width: 26px;
    text-align: center;
}

.wday-dot {
    position: relative;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.15s ease;
    background: var(--dot-bg);
    color: var(--dot-fg);
    box-shadow: 0 0 0 3px var(--dot-ring, transparent);
}

.wday-dot:hover {
    transform: scale(1.15);
}

.wday-num {
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
}

.wday-dot.g {
    --dot-bg: var(--v-success-base);
    --dot-fg: #fff;
    --dot-ring: rgba(0, 153, 102, 0.25);
}

.wday-dot.r {
    --dot-bg: var(--v-error-base);
    --dot-fg: #fff;
    --dot-ring: rgba(232, 92, 92, 0.25);
}

.wday-dot.y {
    --dot-bg: #6b7280;
    --dot-fg: #fff;
    --dot-ring: rgba(107, 114, 128, 0.25);
}

.wday-dot.b {
    --dot-bg: #0284c7;
    --dot-fg: #fff;
    --dot-ring: rgba(2, 132, 199, 0.25);
}

.theme--dark .wday-dot.b {
    --dot-bg: #38bdf8;
    --dot-fg: #0c0e12;
}

.wday-dot.v {
    --dot-bg: #7c3aed;
    --dot-fg: #fff;
    --dot-ring: rgba(124, 58, 237, 0.25);
}

.theme--dark .wday-dot.v {
    --dot-bg: #a78bfa;
    --dot-fg: #0c0e12;
}

.wday-dot.gr {
    --dot-bg: var(--v-surface2-base);
    --dot-fg: var(--v-txtMuted-base);
    border: 1px solid var(--v-outline2-base);
}

.wday-dot.g::after,
.wday-dot.r::after,
.wday-dot.b::after,
.wday-dot.v.done::after,
.wday-dot.v.missed::after {
    position: absolute;
    bottom: -3px;
    right: -3px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid var(--v-surface-base);
    background: var(--dot-bg);
    color: var(--dot-fg);
    font-size: 9px;
    font-weight: 900;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.wday-dot.g::after,
.wday-dot.v.done::after {
    content: '\2713';
}

.wday-dot.r::after,
.wday-dot.v.missed::after {
    content: '\2715';
}

.wday-dot.b::after {
    content: '\25CB';
}

.badge-second {
    position: absolute;
    top: -4px;
    right: -4px;
    background-color: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
    font-size: 9px;
    font-weight: bold;
    line-height: 1;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--v-outline2-base);
}

.tooltip-content {
    text-align: left;
    min-width: 150px;
}

.tt-date {
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 5px;
    text-align: center;
}

.tt-empty {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    text-align: center;
}

.tt-section-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    margin-bottom: 4px;
}

.tt-type {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    margin-bottom: 4px;
}

.tt-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    line-height: 1.7;
}

.tt-row span:last-child {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.tt-divider {
    border: none;
    border-top: 1px solid var(--v-outline-base);
    margin: 6px 0;
}
</style>
